import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {Form} from 'formik';
import classnames from 'classnames';
import FieldInput from '../../components/FieldInput';
import FieldCheckbox from '../../components/FieldCheckbox';
import FieldDatepicker from '../../components/FieldDatepicker';
import FieldFileUpload from '../../components/FieldFileUpload';
import FieldSelectModal from '../../components/FieldSelectModal';
import FieldSelect from '../../components/FieldSelect';
import Button from '../../components/Button';
import ErrorValidation from '../../components/ErrorValidation';
import styles from './styles.module.scss';

const Step2View = ({
                     loadOptions: {
                       gender,
                       citizenship,
                       countries,
                       kupatHolim,
                       familyStatus,
                       cameFrom
                     },
                     values,
                     isValid,
                     submitDisabled,
                     onBirthDateChange,
                     resetCameFromText,
                     regType,
                     handleCheckIdNumber,
                     idNumberUsed
                   }) => {
  const [showAliyahDate, setShowAliyahDate] = useState(Boolean(values.dateOfAliyah) || false);

  useEffect(() => {
    setShowAliyahDate(values.dateOfAliyah)
  }, [values.dateOfAliyah]);

  const removeAliyahDate = () => {
    setShowAliyahDate(false);
    values.dateOfAliyah = null;
  };

  return (
    <Form className={styles.formContainer}>
      <div className={styles.innerPadding}>
        <FieldInput
          fieldId='firstName'
          label={(
            <FormattedMessage
              id='step2.form.firstName.label'
              defaultMessage='First Name'
            />
          )}
          containerStyles={styles.input}
        />
        <FieldInput
          fieldId='lastName'
          label={(
            <FormattedMessage
              id='step2.form.lastName.label'
              defaultMessage='Last Name'
            />
          )}
          containerStyles={styles.input}
        />
        <div className={styles.input}>
          <FieldSelect
            fieldId='gender'
            loadOptions={gender}
            label={(
              <FormattedMessage
                id='step2.form.gender.label'
                defaultMessage='Sex'
              />
            )}
          />
        </div>
        <div className={styles.input}>
          <FieldSelect
            fieldId='citizenship'
            loadOptions={citizenship}
            label={(
              <FormattedMessage
                id='step2.form.citizenship.label'
                defaultMessage='Citizenship'
              />
            )}
          />
        </div>
        {values.citizenship.value !== 2
          ? (
            <>
              <FieldCheckbox
                fieldId={'liveOutsideIsrael'}
                label={(
                  <FormattedMessage
                    id='step2.form.foreignResident.label'
                    defaultMessage='I am a foreign resident, I have not lived in Israel for the last 6 years or more.'
                  />
                )}
                containerStyles={styles.input}
              />
              <div className={styles.fieldIdNumberContainer}>
                <FieldInput
                  fieldId='idNumber'
                  label={(
                    <FormattedMessage
                      id='step2.form.idNumber.label'
                      defaultMessage='ID Number'
                    />
                  )}
                  containerStyles={styles.input}
                  onBlur={() => handleCheckIdNumber(values)}
                />
                {idNumberUsed && (
                  <p className={styles.usedIdNumberMessage}>
                    <FormattedMessage
                      id='step2.form.message.idAlreadyInUse'
                      defaultMessage='ID already in use, please contact customer service'
                    />
                  </p>
                )}
              </div>
              <FieldFileUpload
                accept='.jpg, .jpeg, .png, .bmp'
                fieldId={'uploadId'}
                label={(
                  <FormattedMessage
                    id='step2.form.uploadId.label'
                    defaultMessage='Photocopy of identity card'
                  />
                )}
                cameraLabel={(
                  <FormattedMessage
                    id='common.form.uploadId.note'
                    defaultMessage='Take a photo or upload a file'
                  />
                )}
                containerStyles={styles.input}
                isFileUploaded={values.idFileUploaded}
              />
            </>
          )
          : (
            <>
              <div className={styles.input}>
                <FieldSelectModal
                  fieldId='country'
                  loadOptions={countries}
                  label={(
                    <FormattedMessage
                      id='step2.form.country.label'
                      defaultMessage='Country'
                    />
                  )}
                  modalHeaderLabel={(
                    <FormattedMessage
                      id='step2.form.country.modal.title'
                      defaultMessage='Choice of your country'
                    />
                  )}
                  optionsListLabel={(
                    <FormattedMessage
                      id='step2.form.country.modal.options.label'
                      defaultMessage='Select a country from the list:'
                    />
                  )}
                />
              </div>
              <FieldInput
                fieldId='passport'
                label={(
                  <FormattedMessage
                    id='step2.form.passport.label'
                    defaultMessage='Passport'
                  />
                )}
                containerStyles={styles.input}
              />
              <FieldFileUpload
                fieldId={'uploadPassport'}
                label={(
                  <FormattedMessage
                    id='step2.form.uploadPassport.label'
                    defaultMessage='Passport photo'
                  />
                )}
                cameraLabel={(
                  <FormattedMessage
                    id='common.form.uploadId.note'
                    defaultMessage='Take a photo or upload a file'
                  />
                )}
                containerStyles={styles.input}
                isFileUploaded={values.idFileUploaded}
              />
              <FieldFileUpload
                fieldId={'uploadPermit'}
                label={(
                  <FormattedMessage
                    id='step2.form.uploadPermit.label'
                    defaultMessage='Upload a valid work permit photo'
                  />
                )}
                cameraLabel={(
                  <FormattedMessage
                    id='common.form.uploadId.note'
                    defaultMessage='Take a photo or upload a file'
                  />
                )}
                containerStyles={styles.input}
                isFileUploaded={values.idFileUploaded}
              />
            </>
          )}

        <FieldDatepicker
          fieldId='birthDate'
          label={(
            <FormattedMessage
              id='step2.form.birthday.label'
              defaultMessage='Birthday'
            />
          )}
          containerStyles={styles.input}
          onChange={onBirthDateChange}
        />
        {values.citizenship.value !== 2 && (
          <div className={styles.input}>
            <FieldSelect
              fieldId='kupatHolim'
              loadOptions={kupatHolim}
              label={(
                <FormattedMessage
                  id='step2.form.kupatHulim.label'
                  defaultMessage='Kupat Holim options'
                />
              )}
            />
          </div>
        )}
        <div className={styles.input}>
          <FieldSelect
            fieldId='familyStatus'
            loadOptions={familyStatus}
            label={(
              <FormattedMessage
                id='step2.form.familyStatus.label'
                defaultMessage='Family Status'
              />
            )}
          />
        </div>
        {
          values.familyStatus.value === 2 && (
            <div className={`${styles.input} ${styles.spouse}`}>
              <FieldInput
                fieldId='partnerFirstName'
                label={(
                  <FormattedMessage
                    id='step2.form.spouse.firstName.label'
                    defaultMessage='Spouse First Name'
                  />
                )}
                containerStyles={styles.input}
              />
              <FieldInput
                fieldId='partnerLastName'
                label={(
                  <FormattedMessage
                    id='step2.form.spouse.lastName.label'
                    defaultMessage='Spouse Last Name'
                  />
                )}
                containerStyles={styles.input}
              />
            </div>
          )
        }
      </div>
      <div className={styles.aliyahDate}>
        {showAliyahDate
          ? (
            <FieldDatepicker
              fieldId='dateOfAliyah'
              label={(
                <FormattedMessage
                  id='step2.form.aliyahDate.label'
                  defaultMessage='Aliyah date'
                />
              )}
              info={
                <div onClick={removeAliyahDate}>
                  <FormattedMessage
                    id='step2.form.aliyahDate.button.remove'
                    defaultMessage='Remove'
                  />
                </div>
              }
            />
          )
          : (
            <Button
              variant='secondary'
              onClick={() => setShowAliyahDate(true)}
              label={(
                <FormattedMessage
                  id='step2.form.button.aliyahDate'
                  defaultMessage='+ Aliyah date'
                />
              )}
            />
          )}
      </div>
      <div className={styles.innerPadding}>
        {(regType === 'all') && (
          <div className={classnames(styles.input, styles.cameFrom)}>
            <FieldSelect
              fieldId='cameFrom'
              loadOptions={cameFrom}
              label={(
                <FormattedMessage
                  id='step2.form.cameFrom.label'
                  defaultMessage='How did you hear about us?'
                />
              )}
              containerStyles={styles.input}
              onChange={resetCameFromText}
            />
            {(values.cameFrom?.value === 6) && (
              <FieldInput
                fieldId='cameFromText'
                label={(
                  <FormattedMessage
                    id='step2.form.cameFrom.text.label'
                    defaultMessage='Please specify'
                  />
                )}
              />
            )}
          </div>
        )}
        <Button
          type='submit'
          label={(
            <FormattedMessage
              id='common.form.button.save'
              defaultMessage='Start'
            />
          )}
          disabled={submitDisabled}
        />
        {!isValid && <ErrorValidation/>}
      </div>
    </Form>
  )
};

Step2View.propTypes = {
  loadOptions: PropTypes.shape({
    gender: PropTypes.func,
    citizenship: PropTypes.func,
    countries: PropTypes.func,
    kupatHolim: PropTypes.func,
    familyStatus: PropTypes.func
  }),
  values: PropTypes.object,
  isValid: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  onBirthDateChange: PropTypes.func,
};

export default Step2View;
